import { PlanetLite, PlanetMiddleLite } from '@apeiron/planet'
import { Box, styled, Typography, useMediaQuery } from '@mui/material'
import MobilePlanetSwiper, {
  MobilePlanetSwiperRefHandle,
  MobilePlanetSwiperSlide
} from '@src/components/apostles/share/MobilePlanetSwiper'
import PlanetSelectPopup, {
  FetchParamsCallback
} from '@src/components/planet/list/PlanetSelectPopup'
import ApeironOperateButton from '@src/components/share/apeiron/ApeironOperateButton'
import ApeironUnsavePopup from '@src/components/share/apeiron/ApeironUnsavePopup'
import { OperateButton } from '@src/constants/variant'
import theme from '@src/styles/theme'
import * as R from 'ramda'
import * as RA from 'ramda-adjunct'
import { FC, ReactNode, useEffect, useRef } from 'react'

const MainContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  padding: 30px 20px;
  position: relative;
  ${props => props.theme.breakpoints.up('res768')} {
    background-color: #141415;
    max-width: 1280px;
    width: 100%;
  }
  ${props => props.theme.breakpoints.down('res768')} {
    width: 100vw;
  }
`

const RowContainer = styled(Box)`
  display: flex;
  position: relative;
  ${props => props.theme.breakpoints.up('res768')} {
    flex-direction: column;
    gap: 40px;
  }
  ${props => props.theme.breakpoints.down('res768')} {
    overflow-x: auto;
    flex-direction: row;
    gap: 20px;
  }
`

const HeaderContainer = styled(Box)`
  display: flex;
  gap: 10px;
  justify-content: space-between;
  ${props => props.theme.breakpoints.up('res768')} {
    align-items: center;
    padding: 0 20px;
  }
  ${props => props.theme.breakpoints.down('res768')} {
    align-items: flex-start;
  }
`

const TitleContainer = styled(Box)`
  display: flex;
  flex-direction: column;
`

const HeaderButtonContainer = styled(Box)`
  display: flex;
  flex: 1;
  justify-content: flex-end;
  flex-wrap: wrap;
  ${props => props.theme.breakpoints.up('res480')} {
    gap: 20px;
  }
`

const Title = styled(Typography)`
  color: white;
  font-size: 16px;
  font-weight: 700;
`

const Subtitle = styled(Typography)`
  color: #999999;
  font-size: 14px;
  strong {
    color: #f1b535;
  }
`

const ApostleAttachReforgeLayout: FC<Props> = (props: Props) => {
  const {
    className,
    hasUpdate,
    planetLimit,
    selectLimit,
    selectedPlanets,
    subtitle,
    title,
    confirmButton,
    planetPopup,
    resetButton,
    onCheckHaveChanges,
    onRenderRow,
    onRenderEmptyRow
  } = props

  const lastSelectedPlanetsLengthRef = useRef(0)

  const swiperRef = useRef<MobilePlanetSwiperRefHandle>(null)

  const { text: resetText, onClick: onResetClick } = resetButton || {}

  const { text: confirmText, onClick: onConfirmClick } = confirmButton

  const {
    open,
    onFetchParams: onPlanetFetchParams,
    onDisable: onPlanetDisable,
    onClose: onPlanetClose,
    onSelect: onPlanetSelect,
    onPostProcess: onPlanetPostProcess
  } = planetPopup

  const mobile = useMediaQuery(theme.breakpoints.down('res768'))

  useEffect(() => {
    const isPlanetAdded =
      selectedPlanets.length > lastSelectedPlanetsLengthRef.current

    if (
      selectedPlanets.length > 0 &&
      isPlanetAdded &&
      selectedPlanets.length !== planetLimit
    ) {
      swiperRef.current?.slideTo(selectedPlanets.length - 1)
    }

    lastSelectedPlanetsLengthRef.current = selectedPlanets.length
  }, [planetLimit, selectedPlanets])

  return (
    <>
      <MainContainer className={className}>
        <HeaderContainer>
          <TitleContainer>
            <Title>{title}</Title>
            <Subtitle>{subtitle}</Subtitle>
          </TitleContainer>
          <HeaderButtonContainer>
            {R.isNotNil(resetButton) && (
              <ApeironOperateButton
                disabled={!hasUpdate}
                label={resetText}
                variant={OperateButton.BlueShort}
                onClick={onResetClick}
              />
            )}
            <ApeironOperateButton
              disabled={!hasUpdate}
              label={confirmText}
              variant={OperateButton.BlueShort}
              onClick={onConfirmClick}
            />
          </HeaderButtonContainer>
        </HeaderContainer>
        <>
          {mobile ? (
            <>
              <MobilePlanetSwiper ref={swiperRef}>
                {RA.mapIndexed((planet: PlanetMiddleLite, index: number) => {
                  const key = `${planet.planetID}-${index}`

                  return (
                    <MobilePlanetSwiperSlide key={key}>
                      {onRenderRow(planet, key, index)}
                    </MobilePlanetSwiperSlide>
                  )
                }, selectedPlanets)}
                <MobilePlanetSwiperSlide>
                  {R.length(selectedPlanets) <= planetLimit && (
                    <>{onRenderEmptyRow()}</>
                  )}
                </MobilePlanetSwiperSlide>
              </MobilePlanetSwiper>
            </>
          ) : (
            <RowContainer>
              {RA.mapIndexed((planet: PlanetMiddleLite, index: number) => {
                const key = `${planet.planetID}-${index}`

                return onRenderRow(planet, key, index)
              }, selectedPlanets)}
              {R.length(selectedPlanets) < planetLimit && (
                <>{onRenderEmptyRow()}</>
              )}
            </RowContainer>
          )}
        </>
      </MainContainer>
      <PlanetSelectPopup
        allowSeedPlanet
        open={open}
        selectLimit={selectLimit}
        deriveFetchParams={onPlanetFetchParams}
        disablePlanet={onPlanetDisable}
        onClose={onPlanetClose}
        onSelect={onPlanetSelect}
        onPostProcess={onPlanetPostProcess}
      />
      <ApeironUnsavePopup haveChanges={onCheckHaveChanges} />
    </>
  )
}

type ButtonConfig = {
  text: string
  onClick: () => void
}

type PlanetPopupConfig = {
  open: boolean
  onFetchParams: FetchParamsCallback
  onDisable: (planet: PlanetLite) => string | null
  onClose: () => void
  onSelect: (planets: PlanetLite[]) => void
  onPostProcess: (planet: PlanetLite) => PlanetLite
}

type Props = {
  className?: string
  hasUpdate: boolean
  planetLimit: number
  selectedPlanets: PlanetMiddleLite[]
  selectLimit: number
  subtitle: ReactNode
  title: ReactNode
  confirmButton: ButtonConfig
  resetButton?: ButtonConfig
  planetPopup: PlanetPopupConfig
  onCheckHaveChanges: () => boolean
  onRenderRow: (
    planet: PlanetMiddleLite,
    key: string,
    index: number
  ) => ReactNode
  onRenderEmptyRow: () => ReactNode
}

export default ApostleAttachReforgeLayout
